// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──
import { useEffect, useRef } from "./00-boot.jsx";
import { ACTIVE_LANG, T } from "./04-i18n-dict.jsx";
import { lanacI18nReady } from "./i18n/loader.jsx";
import { lncAnime } from "./05-ui-atoms-theme.jsx";
import { LncDialogHost, MobilePageHeader } from "./13-ui-kit.jsx";
import { App } from "./19-app.jsx";
import { LncGaree, lncGaree, garagePrendreCible } from "./hooks/useGarage.jsx";
function SideItem({icon,label,count,active,color,onClick,indent}){
  {/* Lot Qualité (a11y) : nav latérale focusable clavier (Enter/Espace) + aria-current — attributs additifs, style inchangé. */}
  return <div role="button" tabIndex={0} aria-current={active?"page":undefined} onClick={onClick} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();onClick&&onClick();}}} style={{display:"flex",alignItems:"center",gap:7,padding:indent?"7px 14px 7px 28px":"9px 14px",cursor:"pointer",background:active?"var(--color-success-soft)":"transparent",borderLeft:active?`3px solid ${color||"var(--color-primary)"}`:"3px solid transparent",fontSize:12,color:active?color||"var(--color-primary)":"var(--color-text-secondary)",fontWeight:active?"700":"400"}}>
    {icon&&<span>{icon}</span>}
    <span style={{flex:1}}>{label}</span>
    {count!==undefined&&<span style={{fontSize:11,color:"var(--color-text-secondary)"}}>{count}</span>}
  </div>;
}

// (MobilePageHeader : déplacé dans 13-ui-kit.jsx — audit mobile 30/09/2026, lot 3 — et ré-exporté ici à l'identique.)

// -------------------------------------------------------------------------------
// MOBILE BOTTOM NAVIGATION  — iOS / Vinted style
// -------------------------------------------------------------------------------
//  ⭐ Audit 23/09/2026 (lot client 2, coquille P2-6) — LES ONGLETS DU DOCK SONT DES COMPOSANTS STABLES. `Tab` et `Ic` étaient
//   définis DANS MobileNav : chaque rendu de l'application (un évènement temps réel, une page du fil, une frappe) créait un
//   NOUVEAU type de composant — React démontait et remontait chaque bouton : l'animation du badge (key={badge}) rejouait hors
//   de tout changement, et le focus clavier / lecteur d'écran posé sur un onglet était perdu. Au niveau du module : même
//   DOM, même ORDRE des enfants (`nav :nth-child` porte le mode compact du dock — préservé), et plus aucun remontage.
//  ⭐ (coquille P3-13) Le nom accessible d'un onglet porte son COMPTE : `aria-label` remplaçait le texte du badge — un lecteur
//   d'écran entendait « Messages », sans « 3 non lus ».
function MobileNavTab({id,label,children,badge,dot,onIntent,view,go,badgeWord,dotWord}){
  const active=id==="inbox"?["inbox","messages"].includes(view):view===id;
  const accName=[label,badge>0?`${badge} ${badgeWord||""}`.trim():null,dot&&dotWord?dotWord:null].filter(Boolean).join(" · ");
  return (
    <button className={"mbn-tab"+(active?" mbn-tab--active":"")} onClick={e=>{lncAnime(e.currentTarget,"rebond");go(id);}} onPointerEnter={onIntent} onFocus={onIntent} aria-label={accName} aria-current={active?"page":undefined}>
      <span className="mbn-tab__icon">{children(active)}</span>
      {badge>0&&<span key={badge} className="mbn-badge" aria-hidden="true">{badge>9?"9+":badge}</span>}
      {dot&&!(badge>0)&&<span className="mbn-dot" aria-hidden="true"></span>}
      {dot&&badge>0&&<span className="mbn-dot mbn-dot--with-badge" aria-hidden="true"></span>}
      <span className="mbn-tab__label">{label}</span>
    </button>
  );
}
/* Icône SVG helper — filled quand actif */
function MobileNavIcon({active,paths,filled}){
  return (
    <svg className={"mbn-icon"+(filled?" mbn-icon-fill":"")} viewBox="0 0 24 24" aria-hidden="true"
      style={{stroke:active?"var(--color-success)":"var(--color-text-secondary)",fill:(filled&&active)?"var(--color-success)":"none",fillOpacity:active?0.14:0}}>
      {paths}
    </svg>
  );
}
function MobileNav({view,go,setShowPublish,totalUnread,noticeDot=false,lang,isAdmin=false,adminBadge=0,onPrefetchMsg}){
  const L={
    fr:{home:"Accueil",search:"Rechercher",sell:"Déposer",msg:"Messages",me:"Profil",admin:"Admin",nav:"Navigation",unread:"non lus",news:"nouvelles notifications",pending:"en attente"},
    en:{home:"Home",search:"Search",sell:"Sell",msg:"Inbox",me:"Profile",admin:"Admin",nav:"Navigation",unread:"unread",news:"new notifications",pending:"pending"},
    de:{home:"Start",search:"Suchen",sell:"Inserieren",msg:"Postfach",me:"Profil",admin:"Admin",nav:"Navigation",unread:"ungelesen",news:"neue Benachrichtigungen",pending:"ausstehend"},
    pt:{home:"Início",search:"Pesquisar",sell:"Publicar",msg:"Mensagens",me:"Perfil",admin:"Admin",nav:"Navegação",unread:"não lidas",news:"novas notificações",pending:"pendentes"},
  }[lang]||{home:"Accueil",search:"Rechercher",sell:"Déposer",msg:"Messages",me:"Profil",admin:"Admin",nav:"Navigation"};

  // Pastille active GLISSANTE : une seule pastille qui se déplace vers l'onglet actif (mesuré en JS,
  // robuste au nombre variable d'onglets — admin optionnel — et aux badges qui changent la largeur).
  const navRef=useRef(null), sliderRef=useRef(null), firstSlideRef=useRef(true);
  useEffect(()=>{
    const nav=navRef.current, sl=sliderRef.current;
    if(!nav||!sl) return;
    const place=()=>{
      const active=nav.querySelector(".mbn-tab--active");
      if(!active){ sl.style.opacity="0"; return; }
      sl.style.opacity="1";
      sl.style.width=active.offsetWidth+"px";
      sl.style.height=active.offsetHeight+"px";
      sl.style.transform=`translate(${active.offsetLeft}px, ${active.offsetTop}px)`;
    };
    const placeNoAnim=()=>{const p=sl.style.transition;sl.style.transition="none";place();void sl.offsetWidth;sl.style.transition=p;};
    if(firstSlideRef.current){ placeNoAnim(); firstSlideRef.current=false; } // 1er placement sans glissé depuis 0
    else place();
    window.addEventListener("resize",placeNoAnim);
    return()=>window.removeEventListener("resize",placeNoAnim);
  },[view,totalUnread,noticeDot,adminBadge,isAdmin]);

  //  Audit 23/09 (coquille P2-6) : `Tab`/`Ic` sont les composants du MODULE (stables) — seul le câblage vit ici.
  const Tab=MobileNavTab, Ic=MobileNavIcon;
  const tabProps={view,go};

  return (
    <nav className="mbn-nav" aria-label={L.nav} ref={navRef}>

      {/* Accueil */}
      <Tab id="home" label={L.home} {...tabProps}>
        {a=><Ic active={a} filled paths={<>
          <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z"/>
          <path d="M9 21V13h6v8" style={{fill:"none"}}/>
        </>}/>}
      </Tab>

      {/* Annonces */}
      <Tab id="search" label={L.search} {...tabProps}>
        {a=><Ic active={a} paths={<>
          <circle cx="11" cy="11" r="7"/>
          <line x1="16.5" y1="16.5" x2="21" y2="21"/>
        </>}/>}
      </Tab>

      {/* -- Bouton central Déposer -- */}
      <button className="mbn-sell" onClick={setShowPublish} aria-label={L.sell}>
        <span className="mbn-sell__bubble">
          <svg className="mbn-sell__icon" viewBox="0 0 24 24" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5"  y1="12" x2="19" y2="12"/>
          </svg>
        </span>
        <span className="mbn-sell__label">{L.sell}</span>
      </button>

      {/* Messages — prefetch du chunk messenger AU SURVOL/FOCUS (intention) → ouverture instantanée */}
      <Tab id="inbox" label={L.msg} badge={totalUnread} dot={noticeDot} onIntent={onPrefetchMsg} badgeWord={L.unread} dotWord={L.news} {...tabProps}>
        {a=><Ic active={a} filled paths={
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        }/>}
      </Tab>

      {/* Profil */}
      <Tab id="profile" label={L.me} {...tabProps}>
        {a=><Ic active={a} filled paths={<>
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 20c0-3.8 3.6-7 8-7s8 3.2 8 7" style={{fill:"none"}}/>
        </>}/>}
      </Tab>

      {isAdmin&&<Tab id="admin" label={L.admin} badge={adminBadge} badgeWord={L.pending} {...tabProps}>
        {a=><Ic active={a} paths={<>
          <path d="M12 3 20 6v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6z"/>
          <path d="M8 13h2v4H8zM11 10h2v7h-2zM14 8h2v9h-2z" style={{fill:a?"var(--color-success)":"none",strokeWidth:1.4}}/>
        </>}/>}
      </Tab>}

      {/* Pastille glissante en DERNIER enfant : ne décale PAS les positions des onglets
          (le mode compact du dock masque des onglets par :nth-child → ordre à préserver). */}
      <span className="mbn-slider" ref={sliderRef} aria-hidden="true"/>
    </nav>
  );
}

const sty={
  root:{minHeight:"100vh",background:"var(--color-background)",color:"var(--color-text-primary)",fontFamily:"var(--font-ui)"},
  notif:{position:"fixed",top:72,right:24,padding:"11px 22px",borderRadius:10,color:"var(--on-accent)",fontWeight:"700",zIndex:9999,boxShadow:"0 18px 50px color-mix(in srgb, var(--scrim) 14%, transparent)",fontSize:13},
  hdr:{position:"sticky",top:0,zIndex:100,display:"flex",alignItems:"center",gap:14,padding:"0 20px",height:64,background:"color-mix(in srgb, var(--color-surface) 88%, transparent)",backdropFilter:"blur(12px)",borderBottom:"1px solid var(--color-border)"},
  logo:{display:"flex",alignItems:"center",gap:9,cursor:"pointer",flexShrink:0},
  /* 12/09/2026 — `lMark` et `lAccent` ont disparu : la marque de l'en-tête n'est plus décrite
     ici en pixels (33 px, rayon 9, pastille à top 10 / right 9) mais par la classe partagée
     `.lanac-mark` (src/css/02-type-ui-kit.css), dont la géométrie est dérivée des métriques de
     Fraunces. C'est ce qui empêche une surface de redessiner sa propre version du logo — la
     cause des trois emplacements divergents relevés le 12/09. Le banc l'épingle. */
  lName:{fontSize:16,fontWeight:"900",color:"var(--color-primary)",letterSpacing:"-.5px",lineHeight:1.1},
  lTag:{fontSize:9,color:"var(--color-text-secondary)",lineHeight:1},
  nav:{display:"flex",gap:2,flex:1,overflowX:"auto",minWidth:0},
  nBtn:{padding:"5px 10px",borderRadius:7,border:"none",background:"transparent",color:"var(--color-text-secondary)",cursor:"pointer",fontSize:12,fontWeight:"600",position:"relative",whiteSpace:"nowrap"},
  nAct:{background:"var(--color-success-soft)",color:"var(--color-primary)"},
  nBadge:{position:"absolute",top:-2,right:-2,background:"var(--btn-primary-bg)",color:"var(--on-accent)",borderRadius:10,padding:"1px 5px",fontSize:11,fontWeight:"800"},
  uBtn:{display:"flex",alignItems:"center",gap:6,background:"transparent",border:"none",cursor:"pointer",padding:"3px 7px",borderRadius:8},
  logBtn:{padding:"5px 10px",borderRadius:7,border:"1px solid var(--color-border)",background:"transparent",color:"var(--color-text-secondary)",cursor:"pointer",fontSize:11,fontWeight:"600"},
  pubBtn:{padding:"7px 13px",borderRadius:8,border:"none",background:"linear-gradient(135deg,var(--color-primary),var(--color-primary))",color:"var(--on-brand)",fontWeight:"800",cursor:"pointer",fontSize:12,flexShrink:0},
  page:{maxWidth:1240,margin:"0 auto",padding:"26px 20px"},
  hBtnP:{padding:"10px 20px",borderRadius:9,border:"none",background:"linear-gradient(135deg,var(--color-primary),var(--color-primary))",color:"var(--on-brand)",fontWeight:"800",cursor:"pointer",fontSize:13},
  hBtnS:{padding:"10px 20px",borderRadius:9,border:"1px solid var(--color-success)",background:"transparent",color:"var(--color-success)",fontWeight:"700",cursor:"pointer",fontSize:13},
  hStats:{position:"absolute",right:30,top:"50%",transform:"translateY(-50%)",display:"flex",flexDirection:"column",gap:8},
  sBox:{background:"color-mix(in srgb, var(--color-surface) 88%, transparent)",border:"1px solid var(--color-text-secondary)",borderRadius:10,padding:"10px 15px",textAlign:"center"},
  sN:{display:"block",fontSize:21,fontWeight:"900",color:"var(--color-primary)",letterSpacing:"-1px"},
  sL:{display:"block",fontSize:11,color:"var(--color-text-secondary)",letterSpacing:.3,fontWeight:"600"},
  secT:{fontSize:18,fontWeight:"800",color:"var(--color-primary)",marginBottom:0,letterSpacing:"-.5px"},
  grid:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:13},
};

// --- Error Boundary : filet anti-écran-blanc. PUREMENT ADDITIF — ne s'active que si un
// rendu lève une exception (sinon rend les enfants tels quels). Aucun effet sur les données,
// le réseau, le realtime, R2, la pagination, l'egress, le trust score ou la sécurité. ---
class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = { err:null }; }
  static getDerivedStateFromError(err){ return { err }; }
  componentDidCatch(err, info){
    try { console.error("LANAC render crash:", err, info && info.componentStack); } catch(_){}
    // Observabilité (additif) : remonte le crash de rendu au collecteur in-house si dispo.
    try { window.__lncReport && window.__lncReport("react", err && err.message, { stack: (err && err.stack) || (info && info.componentStack) }); } catch(_){}
  }
  render(){
    if(!this.state.err) return this.props.children;
    return (
      <div role="alert" style={{minHeight:"100dvh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:"32px 22px",textAlign:"center",background:"var(--color-background)",color:"var(--color-text-primary)"}}>
        <div aria-hidden="true" style={{color:"var(--color-error)",lineHeight:0}}><svg viewBox="0 0 24 24" width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="m10.3 3.9-8.1 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3.1l-8-14a2 2 0 0 0-3.4 0z"/></svg></div>
        {/* Audit 23/09/2026 (lot client 2) : le seul écran encore en français seulement (auditeur coquille, i18n) — il s'affiche
            quand l'application a échoué, donc SANS les catalogues : quatre langues en ligne, choisies par ACTIVE_LANG. */}
        <div style={{fontSize:18,fontWeight:800}}>{({fr:"Une erreur est survenue",en:"Something went wrong",de:"Ein Fehler ist aufgetreten",pt:"Ocorreu um erro"})[ACTIVE_LANG]||"Une erreur est survenue"}</div>
        <div style={{fontSize:13,color:"var(--color-text-secondary)",maxWidth:340,lineHeight:1.5}}>{({fr:"L'application a rencontré un problème inattendu. Vos données sont en sécurité — rechargez pour reprendre.",en:"The app ran into an unexpected problem. Your data is safe — reload to continue.",de:"Die App ist auf ein unerwartetes Problem gestoßen. Ihre Daten sind sicher — laden Sie neu, um fortzufahren.",pt:"A aplicação encontrou um problema inesperado. Os seus dados estão seguros — recarregue para continuar."})[ACTIVE_LANG]||"L'application a rencontré un problème inattendu. Vos données sont en sécurité — rechargez pour reprendre."}</div>
        <button onClick={()=>{ try{ window.location.reload(); }catch(_){} }} style={{marginTop:6,padding:"10px 20px",borderRadius:9,border:"none",background:"var(--color-primary)",color:"var(--on-brand)",fontWeight:800,fontSize:13,cursor:"pointer"}}>{({fr:"Recharger",en:"Reload",de:"Neu laden",pt:"Recarregar"})[ACTIVE_LANG]||"Recharger"}</button>
      </div>
    );
  }
}

// ── Phase 1 lazy-load i18n : le PREMIER rendu attend que les dictionnaires de la
//   locale active (+ repli fr) soient chargés — sinon les ~125 accès directs
//   `T[lang].x` rencontreraient des coquilles vides. Le splash STATIQUE de
//   shell.html reste affiché pendant l'attente (~1 fetch same-origin, précaché
//   par le SW dès la 2e visite) → aucune rupture visuelle, aucun FOUC.
//   lanacI18nReady ne rejette JAMAIS et est plafonnée à 8 s (loader) : même si
//   le réseau pend, l'app se rend (tx() retombe sur fr ou la clé) — une UI
//   dégradée VIVANTE plutôt qu'un splash infini. Le filet __lncSelfHeal, lui,
//   lit __lncAppMounted (posé à l'EXÉCUTION du script, 00-boot) : ce différé de
//   rendu ne le déclenche pas.
// ── GARDE ANTI-FOUC (27/07/2026) — la feuille mobile-nav.css est désormais chargée
//   de façon ASYNCHRONE (shell.html : media="print" puis bascule en "all"). Elle
//   ne bloque donc plus le premier pixel… mais rien ne garantirait qu'elle soit
//   APPLIQUÉE quand React monte : sur un cache JS chaud et un réseau lent, l'app
//   se rendrait NON STYLÉE une fraction de seconde (FOUC + saut de mise en page).
//   __lncCssReady (posée par le shell) résout quand la feuille est appliquée,
//   plafonnée à 5 s. Le splash, lui, a ses couleurs en propre (tokens inlinés) :
//   l'attente est invisible. Repli `Promise.resolve()` si le drapeau est absent —
//   cas d'un index.html périmé servi par un SW avant auto-réparation.
// ── LA PAGE GARÉE (10/10/2026 — on ne déconnecte plus un membre inactif, hooks/useGarage) ─────────────────────────────────
//   Une page qui s'est garée (30 minutes sans geste : elle s'est rechargée en le notant dans sessionStorage) ne MONTE PAS
//   l'application : elle rend l'écran de pause, qui n'ouvre rien — ni client de base, ni canal, ni boucle. Le premier geste,
//   le retour de l'écran ou une notification monte l'application dans la MÊME racine, comme à l'ouverture, sur la même adresse.
//   La cible d'une notification gardée par le retour d'une longue absence est rendue ici au chemin du démarrage à froid
//   (window.__lanacNotifTarget, lu par l'application dès qu'elle est prête).
lanacBootReady().then(() => {
  const racine = ReactDOM.createRoot(document.getElementById("root"));
  const ouvrir = () => racine.render(<ErrorBoundary><App /><LncDialogHost /></ErrorBoundary>);
  try { const cible = garagePrendreCible(); if (cible && !window.__lanacNotifTarget) window.__lanacNotifTarget = cible; } catch (_) {}
  if (lncGaree()) racine.render(<LncGaree onReprendre={ouvrir} />); else ouvrir();
});
function lanacBootReady() {
  const css = (typeof window !== "undefined" && window.__lncCssReady) || Promise.resolve();
  return Promise.all([lanacI18nReady, css]);
}


export { ErrorBoundary, MobileNav, MobilePageHeader, SideItem, sty };
